'use client';
import { Address, Province } from '@/api/types/public';
import { useStore } from '@/providers/app-store-provider';
import { font } from '@/theme/zeelool.theme';
import { EPlatform } from '@/utils/enums/platform-enum';
import { env } from '@/utils/env';
import {
  cityValidator,
  firstnameValidator,
  getErrorTranslationKey,
  lastnameValidator,
} from '@/utils/form-rule';
import { hasSpecialChar, parsePhoneValue } from '@/utils/tools';
import { Box, ButtonOwnProps } from '@mui/material';
import { omit } from 'lodash-es';
import { useTranslations } from '@/i18n/use-translations';
import Script from 'next/script';
import { useContext, useEffect, useRef, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import Button from '../Button';
import Input, { type FloatingLabelVariant } from '../Form/FormInput';
import PhoneInput from '../Form/PhoneInput';
import TaxIDInput from '../Form/TaxIDInput';
import ZipCodeInput from '../Form/ZipCodeInput';
import { ResponsiveContext } from '../Responsive/ResponsiveProvider';
import WapOnly from '../Responsive/WapOnly';
import WebOnly from '../Responsive/WebOnly';
import FormItemApartment from './FormItemAparment';
import FormItemCountry from './FormItemCountry';
import FormItemProvince from './FormItemProvince';
import FormItemSetDefault from './FormItemSetDefault';
import FormItemStreet from './FormItemStreet';
import ProvinceDrawer from './MobileProvinceDrawer';

export type AddressFormSubmitHandler = () => void | Promise<boolean | void>;
export type AddressFormSubmitRef = { current: AddressFormSubmitHandler | null };

export interface AddressFormData {
  firstname: string;
  lastname: string;
  country: string;
  country_id: string;
  street: string;
  apartment: string;
  city: string;
  province: string;
  province_id: string | number;
  postcode: string;
  telCode: string;
  telephone: string;
  cpf: string;
  default: number;
  region_code: string;
}

export default function AddressForm({
  data = {
    firstname: '',
    lastname: '',
    country_id: env.NEXT_PUBLIC_ADDRESS_COUNTRY_ID,
    country: env.NEXT_PUBLIC_ADDRESS_COUNTRY,
    street: '',
    apartment: '',
    city: '',
    province: '',
    province_id: '',
    postcode: '',
    tel_code: env.NEXT_PUBLIC_ADDRESS_TEL_CODE,
    tel_country: env.NEXT_PUBLIC_ADDRESS_TEL_COUNTRY,
    telephone: '',
    cpf: '', // 巴西税号
    default: 2,
    region_code: '',
  },
  type = 'ADD',
  bizType = 'shopping',
  close = () => {},
  onBeforeSubmit = () => true,
  onSubmit = () => {},
  onInvalidSubmit = () => {},
  onValuesChange,
  onValuesBlur,
  saveBtnSxPc = {},
  loading = false,
  showDefault = true,
  showCancel = true,
  disabledCountry = false,
  isNeedPagePadding = true,
  stateAndZipLayout = 'horizontal',
  nameLayout,
  formEndContent,
  afterDefaultContent,
  submitLabel,
  hideFooter = false,
  submitRef,
  submitFontFamily = font.bb,
  submitFontSizeWap = '16px',
  floatingLabelVariant,
  overlayZIndex,
  pcInlineFieldWidth,
  pcInlineFieldGap,
  pcInlineFieldGapMode = 'marginLeft',
  pcFooterMarginTop,
  children,
}: {
  data: Address;
  type?: 'ADD' | 'EDIT';
  bizType?: 'shopping' | 'billing';
  showDefault?: boolean;
  showCancel?: boolean;
  saveBtnSxPc?: ButtonOwnProps['sx'];
  close?: () => void;
  onBeforeSubmit?: () => boolean | Promise<boolean>;
  onSubmit?: (address?: Address) => void | Promise<boolean | void>;
  onInvalidSubmit?: () => void;
  onValuesChange?: (values: AddressFormData) => void;
  onValuesBlur?: () => void;
  loading?: boolean;
  disabledCountry?: boolean;
  isNeedPagePadding?: boolean;
  stateAndZipLayout?: 'horizontal' | 'vertical'; // 用来控制在pc端，这两个是两行展示还是一行展示
  /** Allows an embedded PC editor to keep paired fields aligned in two columns. */
  nameLayout?: 'horizontal' | 'vertical';
  formEndContent?: React.ReactNode;
  /** Optional content rendered after the default-address control. */
  afterDefaultContent?: React.ReactNode;
  submitLabel?: React.ReactNode;
  /** Hides the built-in PC/WAP submit footer when the parent owns the action. */
  hideFooter?: boolean;
  /** Exposes the validated submit command to a parent-owned action control. */
  submitRef?: AddressFormSubmitRef;
  submitFontFamily?: string;
  submitFontSizeWap?: string;
  /** Controls the shared input Label presentation mode. */
  floatingLabelVariant?: FloatingLabelVariant;
  /** Raises nested selectors above a parent modal when the form is rendered in a popup. */
  overlayZIndex?: number;
  /** Optional PC two-column field width for compact embedded editors. */
  pcInlineFieldWidth?: string;
  /** Optional PC two-column field gap for compact embedded editors. */
  pcInlineFieldGap?: string;
  /** Controls whether the PC two-column gap belongs to the right field margin or left field padding. */
  pcInlineFieldGapMode?: 'marginLeft' | 'paddingRight';
  /** Optional PC submit footer top spacing for compact embedded editors. */
  pcFooterMarginTop?: string;
  children?: React.ReactNode;
}) {
  const t = useTranslations('address');
  const tValidation = useTranslations('components.address.validation');
  const getTelCode = () => {
    if (!data.tel_code) {
      return '';
    }
    if (data.tel_country && data.tel_code) {
      return `${data.tel_country}(+${data.tel_code})`;
    }
    return `(+${data.tel_code})`;
  };
  const formData = useForm<AddressFormData>({
    defaultValues: {
      firstname: data.firstname,
      lastname: data.lastname,
      country: data.country,
      country_id: data.country_id,
      street: data.street,
      apartment: data.apartment,
      city: data.city,
      province: data.province,
      province_id: data.province_id,
      postcode: data.postcode,
      telCode: getTelCode(),
      telephone: data.telephone,
      cpf: data.cpf,
      default: data.default,
      region_code: data.region_code,
    },
    mode: 'all',
  });
  const onValuesChangeRef = useRef(onValuesChange);
  const onValuesBlurRef = useRef(onValuesBlur);
  const submitHandlerRef = useRef<AddressFormSubmitHandler | null>(null);
  const hasOnValuesChange = Boolean(onValuesChange);

  onValuesChangeRef.current = onValuesChange;
  onValuesBlurRef.current = onValuesBlur;

  useEffect(() => {
    if (!hasOnValuesChange) return;

    return formData.subscribe({
      formState: { values: true },
      callback: ({ values }) => onValuesChangeRef.current?.(values),
    });
  }, [formData.subscribe, hasOnValuesChange]);

  const { deviceType } = useContext(ResponsiveContext);
  const [provinceList, setProvinceList] = useState<Province[]>([]);
  const [showProvinceDialog, setShowProvinceDialog] = useState(false);
  const countryList = useStore((state) => state.countryList);
  // 是否操作过电话号码，操作过之后不再自动改变
  const [telCodeSelected, setTelCodeSelected] = useState(false);
  const getProvinceList = (_country_id: string) => {
    const _country = countryList.find((__country) => __country.country_id === _country_id);
    if (_country) {
      setProvinceList(_country.province || []);
    }
  };

  useEffect(() => {
    getProvinceList(formData.getValues('country_id'));
    if (type !== 'ADD') {
      formData.trigger();
    }
  }, []);

  const handleOnSubmit = async () => {
    const shouldSubmit = await onBeforeSubmit();
    if (!shouldSubmit) return false;

    let isValid = await formData.trigger();
    if (!isValid) {
      onInvalidSubmit();
      return false;
    }
    const addressForm = formData.getValues();
    // 当前处理只针对美国：
    // 传province不传province_id的时候，去匹配Z站地址库中的province是否存在，不存在的时候进行拦截，清空已填入的province内容（只在创建地址，更新地址，支付成功页更改地址位置校验）。
    // 前端在创建地址，更新地址，支付成功页地址编辑，payment页面老地址拦截，4处变更。
    if (addressForm.country_id === 'US' && addressForm.province && !addressForm.province_id) {
      const flag = provinceList.some((item) => item.name === addressForm.province);
      if (!flag) {
        formData.setValue('province', '');
        formData.setValue('province_id', '');
        isValid = await formData.trigger();
      }
    }
    if (isValid) {
      const { telephone, telCode } = parsePhoneValue(addressForm.telephone);
      return await onSubmit({
        ...data,
        ...omit(addressForm, ['telCode', 'telephone']),
        province_id: String(addressForm.province_id), // 确保 province_id 是字符串
        tel_country: telCode.split('(').length === 2 ? telCode.split('(')[0] : undefined,
        tel_code: telCode.replace(/\D/g, ''),
        telephone,
      });
    } else {
      onInvalidSubmit();
      return false;
    }
  };

  submitHandlerRef.current = handleOnSubmit;

  useEffect(() => {
    if (!submitRef) return;

    const submit = () => {
      return submitHandlerRef.current?.();
    };
    submitRef.current = submit;
    return () => {
      if (submitRef.current === submit) submitRef.current = null;
    };
  }, [submitRef]);

  const telCode = useWatch({
    control: formData.control,
    name: 'telCode',
  });

  const lastname = useWatch({
    control: formData.control,
    name: 'lastname',
  });

  const firstname = useWatch({
    control: formData.control,
    name: 'firstname',
  });

  useEffect(() => {
    const _firstname = hasSpecialChar(firstname);
    if (_firstname !== firstname) {
      formData.setValue('firstname', _firstname);
    }
    if (lastname) {
      formData.trigger(['lastname']);
    }
  }, [firstname]);

  useEffect(() => {
    const _lastname = hasSpecialChar(lastname);
    if (_lastname !== lastname) {
      formData.setValue('lastname', _lastname);
    }
    if (firstname) {
      formData.trigger(['firstname']);
    }
  }, [lastname]);
  const isInsideFloatingLabel = floatingLabelVariant === 'inside';
  const isWeb = deviceType === EPlatform.WEB;
  const useHorizontalNameLayout = isWeb && (bizType !== 'billing' || nameLayout === 'horizontal');
  const pcInsideFloatingLabelFormItemClassName =
    isInsideFloatingLabel && isWeb ? 'mb-[20px]' : undefined;
  const pcHorizontalFieldWidth = pcInlineFieldWidth ?? 'calc(50% - 12px)';
  const pcHorizontalFieldGap = pcInlineFieldGap ?? '24px';
  const usePcPaddingRightGap = pcInlineFieldGapMode === 'paddingRight';
  const pcLeadingFieldWidth = usePcPaddingRightGap
    ? `calc(${pcHorizontalFieldWidth} + ${pcHorizontalFieldGap})`
    : pcHorizontalFieldWidth;

  const firstnameField = (
    <Box
      data-address-field="firstname"
      sx={{
        width: useHorizontalNameLayout ? pcLeadingFieldWidth : '100%',
        ...(useHorizontalNameLayout && usePcPaddingRightGap
          ? {
              boxSizing: 'border-box',
              paddingRight: pcHorizontalFieldGap,
            }
          : {}),
      }}
    >
      <Input
        name="firstname"
        label={t('firstName')}
        required
        formItemClassName={pcInsideFloatingLabelFormItemClassName}
        floatingLabelVariant={floatingLabelVariant}
        shrinkLabelWhenFilled={isInsideFloatingLabel}
        control={formData.control}
        rules={{
          validate: () => {
            const errorCode = firstnameValidator(
              formData.getValues('firstname'),
              formData.getValues('lastname'),
            );
            if (errorCode) {
              const translationKey = getErrorTranslationKey(errorCode);
              return translationKey ? tValidation(translationKey as any) : 'Validation error';
            }
            return true;
          },
        }}
      />
    </Box>
  );
  const lastnameField = (
    <Box
      data-address-field="lastname"
      sx={{
        width: useHorizontalNameLayout ? pcHorizontalFieldWidth : '100%',
        marginLeft: useHorizontalNameLayout && !usePcPaddingRightGap ? pcHorizontalFieldGap : '0',
      }}
    >
      <Input
        name="lastname"
        label={t('lastName')}
        required
        formItemClassName={pcInsideFloatingLabelFormItemClassName}
        floatingLabelVariant={floatingLabelVariant}
        shrinkLabelWhenFilled={isInsideFloatingLabel}
        control={formData.control}
        rules={{
          validate: () => {
            const errorCode = lastnameValidator(
              formData.getValues('lastname'),
              formData.getValues('firstname'),
            );
            if (errorCode) {
              const translationKey = getErrorTranslationKey(errorCode);
              return translationKey ? tValidation(translationKey as any) : 'Validation error';
            }
            return true;
          },
        }}
      />
    </Box>
  );
  const countryField = (
    <FormItemCountry
      formData={formData}
      countryList={countryList}
      setProvinceList={setProvinceList}
      telCodeSelected={telCodeSelected}
      disabled={disabledCountry}
      floatingLabelVariant={floatingLabelVariant}
      formItemClassName={pcInsideFloatingLabelFormItemClassName}
      overlayZIndex={overlayZIndex}
    />
  );
  const streetField = (
    <FormItemStreet
      formData={formData}
      countryList={countryList}
      floatingLabelVariant={floatingLabelVariant}
      shrinkLabelWhenFilled={isInsideFloatingLabel}
      formItemClassName={pcInsideFloatingLabelFormItemClassName}
      overlayZIndex={overlayZIndex}
    />
  );
  const apartmentField = (
    <FormItemApartment
      floatingLabelVariant={floatingLabelVariant}
      formData={formData}
      shrinkLabelWhenFilled={isInsideFloatingLabel}
      formItemClassName={pcInsideFloatingLabelFormItemClassName}
    />
  );
  const cityField = (
    <Box data-address-field="city" sx={{ width: '100%' }}>
      <Input
        name="city"
        label={t('city')}
        required
        formItemClassName={pcInsideFloatingLabelFormItemClassName}
        floatingLabelVariant={floatingLabelVariant}
        shrinkLabelWhenFilled={isInsideFloatingLabel}
        control={formData.control}
        slotProps={{ input: { inputProps: { maxLength: 50, autoComplete: 'off' } } }}
        rules={{
          validate: () => {
            const errorCode = cityValidator(formData.getValues('city'));
            if (errorCode) {
              const translationKey = getErrorTranslationKey(errorCode);
              return translationKey ? tValidation(translationKey as any) : 'Validation error';
            }
            return true;
          },
        }}
      />
    </Box>
  );
  const provinceField = (
    <FormItemProvince
      formData={formData}
      provinceList={provinceList}
      stateAndZipLayout={isInsideFloatingLabel && !isWeb ? 'vertical' : stateAndZipLayout}
      floatingLabelVariant={floatingLabelVariant}
      pcInlineFieldWidth={pcHorizontalFieldWidth}
      pcInlineFieldGap={pcHorizontalFieldGap}
      pcInlineFieldGapMode={pcInlineFieldGapMode}
      formItemClassName={pcInsideFloatingLabelFormItemClassName}
      overlayZIndex={overlayZIndex}
    />
  );
  const postcodeField = (
    <Box
      data-address-field="postcode"
      sx={{
        width: isWeb && stateAndZipLayout === 'horizontal' ? pcHorizontalFieldWidth : '100%',
        marginLeft:
          isInsideFloatingLabel && !isWeb
            ? '0'
            : bizType === 'billing' && nameLayout !== 'horizontal'
              ? '0px'
              : !isWeb || stateAndZipLayout !== 'horizontal'
                ? '12px'
                : usePcPaddingRightGap
                  ? '0'
                  : pcHorizontalFieldGap,
      }}
    >
      <ZipCodeInput
        name="postcode"
        label={t('zipPostalCode')}
        required
        formItemClassName={pcInsideFloatingLabelFormItemClassName}
        floatingLabelVariant={floatingLabelVariant}
        shrinkLabelWhenFilled={isInsideFloatingLabel}
        control={formData.control}
        countryId={formData.getValues('country_id')}
        placeholder=""
      />
    </Box>
  );
  const stateAndZipFields = (
    <div className={`w-full flex ${stateAndZipLayout === 'vertical' ? 'flex-col' : 'flex-row'}`}>
      {provinceField}
      {postcodeField}
    </div>
  );
  const paymentNewPhoneInputSx = isInsideFloatingLabel
    ? {
        '& .MuiAutocomplete-inputRoot .MuiAutocomplete-input.MuiAutocomplete-input': {
          padding: '23px 0 4px 12px !important',
        },
        '& .MuiAutocomplete-inputRoot .MuiAutocomplete-endAdornment.MuiAutocomplete-endAdornment': {
          marginTop: '30px',
        },
        '& .MuiFormHelperText-root.MuiFormHelperText-root': {
          margin: '8px 12px 0',
        },
      }
    : undefined;
  const telephoneField = (
    <Box data-address-field="telephone" sx={{ width: '100%' }}>
      <PhoneInput
        name="telephone"
        label={t('telephone')}
        placeholder={t('telephonePlaceholder')}
        required
        formItemClassName={pcInsideFloatingLabelFormItemClassName}
        floatingLabelVariant={floatingLabelVariant}
        control={formData.control}
        countryList={countryList}
        telCode={telCode}
        defaultValue={getTelCode() + data.telephone}
        overlayZIndex={overlayZIndex}
        sx={paymentNewPhoneInputSx}
        onChange={() => {
          if (!telCodeSelected) {
            setTelCodeSelected(true);
          }
        }}
      />
    </Box>
  );
  const taxIdField = ['MX', 'BR'].includes(formData.getValues('country_id')) && (
    <Box sx={{ width: '100%' }}>
      <TaxIDInput
        name="cpf"
        control={formData.control}
        countryId={formData.getValues('country_id')}
        floatingLabelVariant={floatingLabelVariant}
        formItemClassName={pcInsideFloatingLabelFormItemClassName}
        required
      />
    </Box>
  );

  return (
    <>
      <Script
        strategy="afterInteractive"
        src="https://maps.googleapis.com/maps/api/js?key=AIzaSyCX6tjfZXl-IGm-WzLNCaZQRtuaorUoGnw&libraries=places&language=en"
      />
      <WapOnly>
        <ProvinceDrawer
          show={showProvinceDialog}
          close={() => setShowProvinceDialog(false)}
          data={provinceList}
          onSelect={() => {}}
        />
      </WapOnly>
      <FormProvider {...formData}>
        <Box
          component="form"
          autoComplete="off"
          noValidate
          onBlurCapture={() => onValuesBlurRef.current?.()}
          sx={{
            // '& .MuiBox-root': {
            //   marginBottom: deviceType === EPlatform.WEB ? '10px !important' : '8px !important',
            // },
            // TODO 下面的间距被 vw 给覆盖了, 暂时无解，等着改插件
            '& .relative.mb-\\[32px\\]': {
              marginBottom:
                isInsideFloatingLabel && isWeb
                  ? '20px !important'
                  : deviceType === EPlatform.WEB
                    ? '10px !important'
                    : '8px !important',
            },
            '& .relative.mb-\\[24px\\]': {
              marginBottom:
                isInsideFloatingLabel && isWeb
                  ? '20px !important'
                  : deviceType === EPlatform.WEB
                    ? '10px !important'
                    : '8px !important',
            },
            '& .relative.flex.items-baseline': {
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
            },
            '& .absolute.left-0.top-full.form-error-text:not(:empty), & .absolute.left-0.top-full.text-error:not(:empty)':
              {
                position: 'relative',
                textWrap: 'unset',
                lineHeight: '14px',
                ...(isInsideFloatingLabel && { left: '12px' }),
              },
            '& .absolute.left-0.top-full.form-error-text:empty, & .absolute.left-0.top-full.text-error:empty':
              {
                paddingTop: 0,
              },
            ...(isInsideFloatingLabel && {
              '& .MuiFormHelperText-root': {
                margin: '8px 12px 0',
              },
              '& .MuiInputBase-root': {
                minHeight: '50px',
                borderRadius: isWeb ? '4px' : '6px',
              },
              '& .MuiOutlinedInput-root:not(.Mui-focused):not(.Mui-error) .MuiOutlinedInput-notchedOutline':
                {
                  borderColor: isWeb ? '#d1d9e0' : '#d9d9d9',
                },
            }),
          }}
          className={`flex flex-row ${isNeedPagePadding ? '!pl-16 !pr-16' : ''} flex-wrap`}
        >
          {isInsideFloatingLabel && !isWeb ? (
            <>
              {countryField}
              {firstnameField}
              {lastnameField}
              {telephoneField}
              {streetField}
              {apartmentField}
              {cityField}
              {postcodeField}
              {provinceField}
              {taxIdField}
            </>
          ) : (
            <>
              {firstnameField}
              {lastnameField}
              {countryField}
              {streetField}
              {apartmentField}
              {cityField}
              {stateAndZipFields}
              {taxIdField}
              {telephoneField}
            </>
          )}
          {formEndContent}
        </Box>
        {showDefault && (
          <FormItemSetDefault
            formData={formData}
            isNeedPagePadding={isNeedPagePadding}
            variant={isInsideFloatingLabel ? 'paymentNewNoAddress' : 'default'}
          />
        )}
        {afterDefaultContent}
        <WapOnly>{children}</WapOnly>
        {deviceType !== EPlatform.WEB && <div className="h-10" />}
        <WebOnly>
          {!hideFooter && (
            <div
              className={`${isNeedPagePadding ? 'pl-16 pr-16' : ''} flex ${showCancel ? 'justify-between' : 'justify-center'} gap-[24px]`}
              style={{ marginTop: pcFooterMarginTop ?? '18px' }}
            >
              {showCancel && (
                <Button
                  className="flex-1/2 h-60"
                  radiusSize="small"
                  color="primary"
                  variant="outlined"
                  sx={{
                    fontSize: '16px',
                    fontFamily: font.bb,
                  }}
                  onClick={close}
                  loading={loading}
                >
                  {t('cancel')}
                </Button>
              )}
              <Button
                className="flex-1/2 h-60"
                radiusSize="small"
                color="primary"
                variant="contained"
                sx={{
                  fontSize: '16px',
                  fontFamily: submitFontFamily,
                  ...saveBtnSxPc,
                }}
                loading={loading}
                onClick={() => {
                  handleOnSubmit();
                }}
              >
                {submitLabel ?? t('save')}
              </Button>
            </div>
          )}
          <div className="mt-18">{children}</div>
        </WebOnly>

        {!hideFooter && (
          <WapOnly>
            <div
              className={`AddressFormFooter sticky z-2 bottom-0 flex h-60 pl-12 pr-12 justify-between items-center gap-10 bg-white shadow-[0_0_5px_0_#c0c0c080] ${
                !showCancel ? '!pl-0 !pr-0 shadow-none' : ''
              }`}
            >
              {showCancel && (
                <Button
                  className="flex-1/2 h-40"
                  radiusSize="large"
                  color="primary"
                  variant="outlined"
                  sx={{
                    fontSize: '16px',
                    fontFamily: font.bb,
                  }}
                  onClick={close}
                  loading={loading}
                >
                  {t('cancel')}
                </Button>
              )}
              <Button
                className="flex-1/2 h-40"
                radiusSize="large"
                color="primary"
                variant="contained"
                sx={{
                  fontSize: submitFontSizeWap,
                  fontFamily: submitFontFamily,
                }}
                loading={loading}
                onClick={() => {
                  handleOnSubmit();
                }}
              >
                {submitLabel ?? t('save')}
              </Button>
            </div>
          </WapOnly>
        )}
      </FormProvider>
    </>
  );
}
